// ─── src/components/gewLive/LiveCharts.tsx ───────────────────────────────────
//
// Charts for GEW Live. Two-series charts compare registered vs attended in
// fixed colours (validated on the dark card surface: blue #5B8DEF,
// green #0FA57A); one-series charts use the attended green. Every chart
// has a hover tooltip; two-series charts carry a legend.

import type { FC, ReactNode } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, Legend,
} from "recharts";
import type { DemoRow, LiveSnapshot, RankRow } from "../../api/gewLive";

export const REG = "#5B8DEF";
export const ATT = "#0FA57A";
const AXIS = { fill: "#9ca3af", fontSize: 11 };
const GRID = "#1f2937";
const CURSOR = { fill: "rgba(255,255,255,0.04)" };

const fmt = (n: number) => n.toLocaleString("en-US");
const DAY_LABEL: Record<string, string> = {
  "2026-11-01": "Sun 1 Nov",
  "2026-11-02": "Mon 2 Nov",
  "2026-11-03": "Tue 3 Nov",
  "2026-11-04": "Wed 4 Nov",
  "2026-11-05": "Thu 5 Nov",
};
export const dayLabel = (d: string) => DAY_LABEL[d] ?? d;

export const ChartCard: FC<{ title: string; subtitle?: string; children: ReactNode; height?: number; empty?: boolean }> = ({
  title, subtitle, children, height = 260, empty,
}) => (
  <div className="card p-5">
    <p className="text-white font-semibold text-sm">{title}</p>
    {subtitle && <p className="text-brand-muted text-xs mt-0.5">{subtitle}</p>}
    <div className="mt-4" style={{ height }}>
      {empty ? (
        <div className="h-full flex items-center justify-center text-brand-muted text-sm">No check-ins yet.</div>
      ) : (
        children
      )}
    </div>
  </div>
);

const Tip: FC<{ active?: boolean; payload?: { name: string; value: number; color: string; payload: Record<string, unknown> }[]; label?: string | number; render?: (p: Record<string, unknown>) => ReactNode }> = ({
  active, payload, label, render,
}) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-brand-card border border-brand-border rounded-xl px-4 py-3 shadow-xl text-sm" dir="ltr">
      <p className="text-white font-semibold mb-1" dir="auto">{label}</p>
      {render
        ? render(payload[0].payload)
        : payload.map((p) => (
            <p key={p.name} className="text-brand-muted flex items-center gap-2">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />
              {p.name}: <span className="text-white font-medium">{fmt(p.value)}</span>
            </p>
          ))}
    </div>
  );
};

/** Legend in series order (registered first), drawn as plain HTML. */
const SeriesLegend: FC<{ payload?: { value: string; color: string }[] }> = ({ payload }) => (
  <div className="flex justify-end gap-4 text-xs text-brand-muted pb-2">
    {[...(payload ?? [])].sort((a, b) => Number(b.color === REG) - Number(a.color === REG)).map((p) => (
      <span key={p.value} className="inline-flex items-center gap-1.5">
        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />
        {p.value}
      </span>
    ))}
  </div>
);
const legend = <Legend verticalAlign="top" height={28} content={<SeriesLegend />} />;

const ARABIC = /[\u0600-\u06FF]/;
/**
 * Category-axis label that keeps Arabic text in the right order: SVG text
 * is laid out left-to-right by default, which scrambles "18 أو أقل".
 */
const CategoryTick: FC<{ x?: number; y?: number; payload?: { value: string }; max?: number }> = ({ x = 0, y = 0, payload, max = 34 }) => {
  const v = String(payload?.value ?? "");
  const text = v.length > max ? v.slice(0, max) + "…" : v;
  const rtl = ARABIC.test(v);
  return (
    <text x={x} y={y} dy={4} fill="#9ca3af" fontSize={11} textAnchor={rtl ? "start" : "end"} direction={rtl ? "rtl" : "ltr"}>
      <title>{v}</title>
      {text}
    </text>
  );
};

/** Registered vs attended per GEW day, with the number of events in the tooltip. */
export const DaysChart: FC<{ days: LiveSnapshot["days"] }> = ({ days }) => {
  const data = days.map((d) => ({ ...d, label: dayLabel(d.day) }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -8 }} barGap={2} barCategoryGap="28%">
        {legend}
        <CartesianGrid vertical={false} stroke={GRID} />
        <XAxis dataKey="label" tick={AXIS} axisLine={false} tickLine={false} />
        <YAxis allowDecimals={false} tick={AXIS} axisLine={false} tickLine={false} tickFormatter={fmt} />
        <Tooltip
          cursor={CURSOR}
          content={
            <Tip
              render={(p) => (
                <div className="text-brand-muted space-y-0.5">
                  <p>Events: <span className="text-white">{p.events as number}</span> ({p.done as number} done)</p>
                  <p><Sw c={REG} />Registered: <span className="text-white">{fmt(p.registered as number)}</span></p>
                  <p><Sw c={ATT} />Attended: <span className="text-white">{fmt(p.attended as number)}</span></p>
                </div>
              )}
            />
          }
        />
        <Bar dataKey="registered" name="Registered" fill={REG} radius={[4, 4, 0, 0]} maxBarSize={36} />
        <Bar dataKey="attended" name="Attended (checked in)" fill={ATT} radius={[4, 4, 0, 0]} maxBarSize={36}>
          <LabelList dataKey="attended" position="top" fill="#9ca3af" fontSize={11} formatter={(v: unknown) => (Number(v) ? fmt(Number(v)) : "")} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const Sw: FC<{ c: string }> = ({ c }) => (
  <span className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-middle" style={{ background: c }} />
);

/** Check-ins per hour of the day (Damascus time). */
export const ArrivalsChart: FC<{ arrivals: LiveSnapshot["arrivals"] }> = ({ arrivals }) => {
  const data = arrivals.map((a) => ({ ...a, label: `${String(a.hour).padStart(2, "0")}:00` }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 16, right: 8, bottom: 0, left: -8 }} barCategoryGap="18%">
        <CartesianGrid vertical={false} stroke={GRID} />
        <XAxis dataKey="label" tick={AXIS} axisLine={false} tickLine={false} interval="preserveStartEnd" />
        <YAxis allowDecimals={false} tick={AXIS} axisLine={false} tickLine={false} />
        <Tooltip cursor={CURSOR} content={<Tip />} />
        <Bar dataKey="count" name="Check-ins" fill={ATT} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

/**
 * Registered vs attended profile for one attribute. Bars are each group's
 * share of its own total, so the two profiles compare even though far
 * fewer people attend than register; counts are in the tooltip.
 */
export const ProfileChart: FC<{ rows: DemoRow[] }> = ({ rows }) => {
  const regTotal = rows.reduce((s, r) => s + r.registered, 0);
  const attTotal = rows.reduce((s, r) => s + r.attended, 0);
  const share = (n: number, t: number) => (t ? Math.round((n / t) * 1000) / 10 : 0);
  const data = rows
    .filter((r) => r.registered || r.attended)
    .map((r) => ({ ...r, regPct: share(r.registered, regTotal), attPct: share(r.attended, attTotal) }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 36, bottom: 0, left: 8 }} barGap={2} barCategoryGap="24%">
        {legend}
        <CartesianGrid horizontal={false} stroke={GRID} />
        <XAxis type="number" tick={AXIS} axisLine={false} tickLine={false} unit="%" domain={[0, "auto"]} />
        <YAxis type="category" dataKey="label" width={150} tick={<CategoryTick />} axisLine={false} tickLine={false} interval={0} />
        <Tooltip
          cursor={CURSOR}
          content={
            <Tip
              render={(p) => (
                <div className="text-brand-muted space-y-0.5">
                  <p><Sw c={REG} />Registered: <span className="text-white">{fmt(p.registered as number)}</span> ({p.regPct as number}%)</p>
                  <p><Sw c={ATT} />Attended: <span className="text-white">{fmt(p.attended as number)}</span> ({p.attPct as number}%)</p>
                </div>
              )}
            />
          }
        />
        <Bar dataKey="regPct" name={`Registered (${fmt(regTotal)})`} fill={REG} radius={[0, 4, 4, 0]} maxBarSize={14} />
        <Bar dataKey="attPct" name={`Attended (${fmt(attTotal)})`} fill={ATT} radius={[0, 4, 4, 0]} maxBarSize={14}>
          <LabelList dataKey="attPct" position="right" fill="#9ca3af" fontSize={11} formatter={(v: unknown) => (Number(v) ? `${v}%` : "")} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

/** One-series horizontal bars with the value at the end of each bar. */
export const RankBars: FC<{ rows: { label: string; value: number; hint?: string }[]; unit?: string; labelWidth?: number }> = ({
  rows, unit = "", labelWidth = 170,
}) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 44, bottom: 0, left: 8 }} barCategoryGap="22%">
        <CartesianGrid horizontal={false} stroke={GRID} />
        <XAxis type="number" tick={AXIS} axisLine={false} tickLine={false} allowDecimals={false} unit={unit} />
        <YAxis type="category" dataKey="label" width={labelWidth} tick={<CategoryTick max={32} />} axisLine={false} tickLine={false} interval={0} />
        <Tooltip
          cursor={CURSOR}
          content={
            <Tip
              render={(p) => (
                <div className="text-brand-muted space-y-0.5">
                  <p><Sw c={ATT} /><span className="text-white">{fmt(p.value as number)}{unit}</span></p>
                  {p.hint ? <p>{p.hint as string}</p> : null}
                </div>
              )}
            />
          }
        />
        <Bar dataKey="value" fill={ATT} radius={[0, 4, 4, 0]} maxBarSize={18}>
          <LabelList dataKey="value" position="right" fill="#9ca3af" fontSize={11} formatter={(v: unknown) => `${fmt(Number(v))}${unit}`} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export const rankRows = (rows: RankRow[], by: "attended" | "showUpRate") =>
  rows.map((r) => ({
    label: r.title,
    value: by === "attended" ? r.attended : r.showUpRate ?? 0,
    hint: `${r.organizer} · ${fmt(r.attended)} of ${fmt(r.accepted)} accepted`,
  }));
